import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Grid } from "@mui/material";
import { IQualificationCheckResult, IUser } from "@vmosolutions/oc-interfaces";

import { getQualifications } from "src/API/CrewScheduleErrors";
import { FilterSearchTable } from "src/components/FilterSearchTable/FilterSearchTable";
import { ITableColumn } from "src/components/FilterSearchTable/TableComponents/TableHeader";
import { setFocusedCrewSchedArea, setSelectedCrewDays } from "src/redux/actions/CrewSchedule/CrewSchedule.Actions";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { IQualificationMap } from "src/redux/reducers/CrewScheduleReducer";
import moment from "moment-timezone";
import { SpinnerProgress } from "src/components/SpinnerProgress/SpinnerProgress";
import classNames from "classnames";
import { getFirstCrewSchedWindowIdContainingCrew } from "../CrewSchedule/CrewScheduleHeader/CrewScheduleHeader";

interface IQualificationErrorProps {
  id: string;
}

export const qualificationErrorTableInfo: ITableColumn[] = [
  {
    id: "title",
    value: "title",
    index: 0,
    numeric: false,
    label: "Title",
    type: "string",
    width: 10,
    visible: true,
    filter: true,
  },
  {
    id: "crewMember",
    value: "crewMember",
    index: 1,
    numeric: false,
    label: "Crew Member",
    type: "string",
    width: 10,
    visible: true,
    filter: false,
  },
  {
    id: "start",
    value: "start",
    index: 2,
    numeric: false,
    label: "Start",
    type: "date",
    width: 10,
    visible: true,
    filter: false,
  },
  {
    id: "end",
    value: "end",
    index: 3,
    numeric: false,
    label: "End",
    type: "date",
    width: 10,
    visible: true,
    filter: false,
  },

  // {
  //   id: "priority",
  //   value: "priority",
  //   index: 2,
  //   numeric: false,
  //   label: "Priority",
  //   type: "string",
  //   width: 10,
  //   visible: true,
  //   filter: false,
  // },
];

export const qualificationErrorsDesc = {
  tableTitle: "Qualification Errors",
  tableDescription: "",
  noMessage: "No Errors found",
};

export const QualificationErrorTable = (props: IQualificationErrorProps): JSX.Element => {
  const { id } = props;

  const dispatch = useDispatch();

  const appUsers = useSelector((state: IRootState) => state.structures.appUsers);
  const crewQualifications = useSelector((state: IRootState) => state.crewSchedule.crewQualifications);
  const filteredCrew = useSelector((state: IRootState) => state.crewSchedule.filteredCrew);
  const isLoading = useSelector((state: IRootState) => state.crewSchedule.isLoadingCrewQualifications);

  const [warning, setWarning] = useState<string>("");

  const qualificationErrors = useMemo(() => crewQualifications, [crewQualifications]);

  const getQualificationsAction = useCallback((): void => {
    dispatch(getQualifications());
  }, [dispatch]);

  useEffect(() => {
    //will this cause a loop if there is nothing in the quals cache
    //check on is loading so that you only make this call once
    if (!crewQualifications && !isLoading) {
      getQualificationsAction();
    }
  }, [crewQualifications, getQualificationsAction, isLoading]);

  const qualificationTableErrors = useMemo(() => {
    if (!qualificationErrors) {
      return null;
    }
    return Object.values(qualificationErrors).flatMap((val: { qualifications: IQualificationMap; createdAt: number }) =>
      Object.values(val.qualifications)
    );
  }, [qualificationErrors]);

  const data = useMemo(() => {
    const quals: any[] = [];
    if (!qualificationTableErrors) {
      return quals;
    }
    qualificationTableErrors.forEach((vals: IQualificationCheckResult[]) => {
      vals.forEach((val: IQualificationCheckResult) => {
        const user = appUsers.find((appUser: IUser) => appUser._id === val.crewId);
        const crewMember = user ? `${user.firstName} ${user.lastName} [${user.employeeId}]` : "";
        quals.push({
          title: val.title,
          regulation: val.regulation,
          crewMember,
          start: val.period?.start,
          end: val.period?.end,
          crewId: val.crewId,
        });
      });
    });
    return quals;
  }, [appUsers, qualificationTableErrors]);

  const changeSelectedRows = useCallback(
    (rows: any, focusSelected?: boolean, dataType?: string) => {
      const row = rows[0];
      const crewId = row?.crewId;
      const selectedDay = moment.utc(row?.start).startOf("day")?.valueOf();
      const endOfDay = moment.utc(row?.end).endOf("day")?.valueOf();
      if (focusSelected) {
        const windowToFocus = getFirstCrewSchedWindowIdContainingCrew(crewId, filteredCrew);
        if (!windowToFocus) {
          console.warn("You have no open crew schedule window displaying this crew member");
        }
        if (!!windowToFocus || !crewId) {
          setWarning("");
        } else {
          setWarning("You have no open crew schedule window displaying this crew member");
        }
        //do I need to dispatch if there is no
        if (windowToFocus) {
          dispatch(
            setFocusedCrewSchedArea(
              {
                timeInterval: [selectedDay, endOfDay],
                crewIds: [crewId],
              },
              windowToFocus
            )
          );
        }
      }
      dispatch(setSelectedCrewDays({ crewId, selectedTimes: [selectedDay], timezone: "Etc/UTC" }));
    },
    [dispatch, filteredCrew]
  );
  const tableData = useMemo(() => {
    return [
      {
        id: "qualificationErrors",
        dataType: "QualificationErrors",
        label: "Qualification Errors",
        data,
        tableInfo: qualificationErrorTableInfo,
        tableInfoDisplay: qualificationErrorsDesc,
      },
    ];
  }, [data]);

  if (data?.length === 0) {
    return (
      <Grid item className="no_errors_block">
        <SpinnerProgress
          className={classNames("loading_spinner", isLoading && "isLoading")}
          loading={isLoading}
          message="loading..."
        />
        {!isLoading ? "No Qualification Errors Found" : ""}
      </Grid>
    );
  }

  return (
    <Grid item className="errors_table__block">
      {!!warning && <span className="error_table_warning">{warning}</span>}
      <FilterSearchTable
        className="error_table"
        id={`qualification_errors_table_${id}`}
        parentId={`${id}-panel`}
        events={tableData}
        uniqueKey={"checkId"}
        changeSelectedRows={changeSelectedRows}
        selectModeSingle={true}
        defaultRowsPerPage={10}
        // hideSelectionCheckboxes={true}
        // hideFocusSelected={true}
        // hideShowColumns={true}
        // stickyHeader={true} //! Sticker header causes issues when used with table controls selection menus.
      />
    </Grid>
  );
};
